import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * Dialogs currently holding the body scroll lock. Counted rather than read back off
 * `document.body.style`, because dialogs stack and both the lock and its release are deferred
 * — see the dialog scroll lock entry in .tf-rules/learnings.md.
 *
 * @type {Set<DialogComponent>}
 */
const scrollLockHolders = new Set();

/** The scroll position frozen when the first holder took the lock. */
let lockedScrollY = 0;

/**
 * Claims the lock, freezing the page on the first claim. Called synchronously so two dialogs
 * opening in one tick can't both believe they were first.
 *
 * @param {DialogComponent} dialog
 * @returns {boolean} Whether this claim should write the styles.
 */
function acquireScrollLock(dialog) {
  const isFirst = scrollLockHolders.size === 0;

  if (isFirst) lockedScrollY = window.scrollY;
  scrollLockHolders.add(dialog);

  return isFirst;
}

/**
 * Drops the claim, restoring scroll only once nothing else holds it.
 *
 * @param {DialogComponent} dialog
 */
function releaseScrollLock(dialog) {
  if (!scrollLockHolders.delete(dialog) || scrollLockHolders.size > 0) return;

  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';
  window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    // Safety net for closes that bypass `closeDialog` — a native dismissal, or a
    // `dialog.close()` from app code — which would otherwise hold the lock forever.
    this.refs.dialog?.addEventListener('close', this.#releaseLock);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    this.refs.dialog?.removeEventListener('close', this.#releaseLock);
    this.#releaseLock();
  }

  #releaseLock = () => releaseScrollLock(this);

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const shouldLock = acquireScrollLock(this);
    const scrollY = lockedScrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      if (shouldLock) {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
      }

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('pointerdown', this.#handlePointerDown);
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    releaseScrollLock(this);

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Tracks whether the press that begins a click started outside the dialog.
   * @type {boolean}
   */
  #pointerDownedOutside = false;

  /**
   * Records where a press started so a click can only close the dialog when the
   * press both started and ended outside it.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  #handlePointerDown = (event) => {
    this.#pointerDownedOutside = isClickedOutside(event, this.refs.dialog);
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * Requires the press to have started outside too: otherwise a DOM change
   * mid-interaction (e.g. a facet re-render removing the clicked control) can
   * detach the click target and be misread as an outside/backdrop click, closing
   * the dialog unexpectedly.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (this.#nestedDialogOpen) return;

    if (this.#pointerDownedOutside && isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Whether a dialog nested inside this one is open, and so owns dismissal.
   * @type {boolean}
   */
  get #nestedDialogOpen() {
    return this.refs.dialog?.querySelector('dialog[open]') != null;
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    // Escape belongs to the innermost open dialog, and it closes itself.
    if (this.#nestedDialogOpen) return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true },
);
